import { useEffect } from "react";

import { useAuthStore } from "../stores/useAuthStore";
import { useCan } from "./useCan.js";
import {
  fetchAndCacheWaypoints,
  loadCachedWaypointsAsync,
  subscribeToWaypointsChanges,
} from "../utils/waypointsCache.js";

const ANDROID_RESUME_SYNC_DELAY_MS = 30000;

function getAndroidResumeSyncDelayMs() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return 0;
  if (!/Android/i.test(navigator.userAgent || "")) return 0;
  const resumeStartedAt = Number(window.__TREKKIN_ANDROID_RESUME_PERF?.startedAt || 0);
  const foregroundAt = Number(window.__TREKKIN_ANDROID_FOREGROUND_AT || 0);
  const startedAt = Math.max(resumeStartedAt, foregroundAt);
  if (!startedAt) return 0;
  const elapsed = Date.now() - startedAt;
  if (!Number.isFinite(elapsed) || elapsed < 0) return 0;
  return Math.max(0, ANDROID_RESUME_SYNC_DELAY_MS - elapsed);
}

export default function useWaypointsSync(options = {}) {
  const enabled = options?.enabled ?? true;
  const user = useAuthStore((s) => s.user);
  const can = useCan();
  const canOpenWaypoints = can("PanelWaypoints", "open");

  useEffect(() => {
    if (!enabled) return;
    if (!user || !canOpenWaypoints) return;

    let active = true;

    let delayedRefreshTimer = null;

    // Hidrata imediatamente a cópia privada; não espera por uma tentativa de rede.
    loadCachedWaypointsAsync().catch(() => {});

    // Mantém a cópia local alinhada sempre que a versão/dados no RTDB mudam.
    const unsubscribeChanges = subscribeToWaypointsChanges((err) => {
      console.warn("⚠️ Listener de waypoints indisponível:", err);
    });

    const refresh = async (force = false) => {
      if (!active) return;
      const resumeDelayMs = getAndroidResumeSyncDelayMs();
      if (!force && resumeDelayMs > 0) {
        if (delayedRefreshTimer) window.clearTimeout(delayedRefreshTimer);
        delayedRefreshTimer = window.setTimeout(() => {
          delayedRefreshTimer = null;
          refresh(false);
        }, resumeDelayMs + 100);
        return;
      }
      try {
        await fetchAndCacheWaypoints({ force });
      } catch (err) {
        console.warn("⚠️ Atualização dos waypoints falhou:", err);
      }
    };

    const handleRefresh = () => refresh(true);
    const handleVisible = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState === "visible") {
        if (typeof window !== "undefined") {
          window.__TREKKIN_ANDROID_FOREGROUND_AT = Date.now();
        }
        refresh();
      }
    };
    const handleOnline = () => refresh();

    const scheduleInitial = () => {
      if (typeof window === "undefined") return;
      if (
        typeof navigator !== "undefined" &&
        /Android/i.test(navigator.userAgent || "")
      ) {
        window.__TREKKIN_ANDROID_FOREGROUND_AT = Date.now();
      }
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(() => refresh());
      } else {
        window.setTimeout(() => refresh(), 1200);
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("trekkin:waypoints-refresh", handleRefresh);
      window.addEventListener("online", handleOnline);
      document.addEventListener("visibilitychange", handleVisible);
      scheduleInitial();
    }

    return () => {
      active = false;
      if (delayedRefreshTimer && typeof window !== "undefined") {
        window.clearTimeout(delayedRefreshTimer);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("trekkin:waypoints-refresh", handleRefresh);
        window.removeEventListener("online", handleOnline);
        document.removeEventListener("visibilitychange", handleVisible);
      }
      unsubscribeChanges?.();
    };
  }, [enabled, user?.uid, canOpenWaypoints]);
}
